/**
 * Shop Filters block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS.
 *
 * The bar filters without this file: it is a GET form of checkboxes with an
 * Apply button, and every name is one the catalogue endpoint already reads. All
 * this adds is doing it without the page reload — fetch the endpoint, swap the
 * grid, rewrite the address bar — plus the chips and the mobile drawer, which
 * are conveniences rather than the mechanism.
 *
 * Everything it needs is already in the markup: the endpoint on the form, the
 * facet names on each disclosure. Nothing is passed through wp_localize_script,
 * so this file has no build-time dependency on PHP.
 */

import './index.scss';

const form = document.querySelector('.shop-filters');
const grid = document.querySelector('.shop-archive__items');

/**
 * Read the ticked options, facet by facet.
 *
 * @param {HTMLFormElement} node The filter form.
 * @returns {Map<string, string[]>} Facet name => chosen slugs.
 */
function selection(node) {
    const picked = new Map();

    for (const box of node.querySelectorAll('.shop-filters__option input:checked')) {
        /* `material[]` is the shape a form submission needs; the endpoint and
         * the address bar both want the bare name. */
        const facet = box.name.replace(/\[\]$/, '');

        picked.set(facet, [...(picked.get(facet) ?? []), box.value]);
    }

    return picked;
}

/**
 * Build the query string for a selection.
 *
 * Comma-joined rather than repeated, because this string is what somebody sees
 * and copies out of the address bar.
 *
 * @param {Map<string, string[]>} picked Facet name => chosen slugs.
 * @param {string}                sort   Sort key.
 * @returns {URLSearchParams} The query.
 */
function query(picked, sort) {
    const params = new URLSearchParams();

    for (const [facet, slugs] of picked) {
        params.set(facet, slugs.join(','));
    }

    if (sort && 'relevance' !== sort) {
        params.set('sort', sort);
    }

    return params;
}

/**
 * The query to send, which is the shareable one plus the page size.
 *
 * `per_page` is deliberately not in the address bar: it is a rendering detail
 * the server already decided, and a URL people pass around should carry what
 * was chosen, not how it happens to be laid out.
 *
 * @param {URLSearchParams} params The shareable query.
 * @param {HTMLFormElement} node   The filter form.
 * @returns {URLSearchParams} The query for the endpoint.
 */
function request(params, node) {
    const full = new URLSearchParams(params);

    /* The archive this bar sits on — a category page carries its category in
     * the path, which the endpoint never sees. Added underneath the ticked
     * facets so a category ticked in the bar still wins. */
    for (const [key, value] of new URLSearchParams(node.dataset.context ?? '')) {
        if (!full.has(key)) {
            full.set(key, value);
        }
    }

    full.set('per_page', node.dataset.perPage ?? '12');

    return full;
}

/**
 * Keep every "how many chosen" figure in step with the boxes.
 *
 * The badges and the total were printed once by the server and never touched
 * again, so ticking a box added to them and unticking never took anything away:
 * the bar claimed three filters while one was on. They are counted from the
 * checkboxes themselves, which is the only source that cannot drift.
 *
 * @param {HTMLElement} node The filter form.
 * @returns {void}
 */
function tally(node) {
    let all = 0;

    for (const facet of node.querySelectorAll('.shop-filters__field')) {
        const on = facet.querySelectorAll('.shop-filters__option input:checked').length;
        const badge = facet.querySelector('[data-badge]');

        all += on;

        if (badge) {
            badge.textContent = String(on);
            badge.hidden = 0 === on;
        }
    }

    const total = node.querySelector('.shop-filters__toggle-count');

    if (total) {
        total.dataset.picks = String(all);
        total.textContent = all ? `(${all})` : '';
    }
}

/**
 * Redraw the row of chips for what is currently ticked.
 *
 * @param {HTMLFormElement} node The filter form.
 * @returns {void}
 */
function chips(node) {
    const rail = node.querySelector('.shop-filters__chips');

    if (!rail) {
        return;
    }

    const boxes = [...node.querySelectorAll('.shop-filters__option input:checked')];

    rail.textContent = '';
    rail.hidden = 0 === boxes.length;

    for (const box of boxes) {
        const chip = document.createElement('button');

        chip.type = 'button';
        chip.className = 'shop-filters__chip';
        chip.textContent =
            box.closest('.shop-filters__option')?.querySelector('.shop-filters__option-label')
                ?.textContent ?? box.value;

        chip.addEventListener('click', () => {
            box.checked = false;
            apply(node);
        });

        rail.append(chip);
    }

    if (boxes.length > 1) {
        const clear = document.createElement('button');

        clear.type = 'button';
        clear.className = 'shop-filters__clear';
        /* Wording comes from the server, translated; the fallback exists only
         * for markup that predates the attribute. */
        clear.textContent = rail.dataset.clear ?? 'Clear all';

        clear.addEventListener('click', () => {
            for (const box of boxes) {
                box.checked = false;
            }

            /* The badges count ticks, so they have to be recounted here too —
             * clearing the boxes without this left every dropdown still
             * claiming the filters it no longer had. */
            tally(node);
            apply(node);
        });

        rail.append(clear);
    }

    /* The totals and the per-facet badges are counted in one place; see tally(). */
    tally(node);
}

/**
 * Apply the current selection: fetch, swap the grid, rewrite the address.
 *
 * Anything unexpected — a dead network, a shape the endpoint did not promise —
 * submits the form the ordinary way rather than leaving the reader looking at
 * results that no longer match what they ticked.
 *
 * @param {HTMLFormElement} node The filter form.
 * @returns {Promise<void>}
 */
async function apply(node) {
    const sort = node.querySelector('[name="sort"]')?.value ?? 'relevance';
    const params = query(selection(node), sort);

    chips(node);

    if ('true' !== node.dataset.live || !grid) {
        HTMLFormElement.prototype.submit.call(node);
        return;
    }

    node.setAttribute('aria-busy', 'true');

    try {
        const response = await fetch(`${node.dataset.endpoint}?${request(params, node)}`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        });
        const payload = await response.json();

        if (!response.ok || 'string' !== typeof payload.html) {
            throw new Error('unexpected response');
        }

        grid.innerHTML = payload.html;

        counts(node, payload.counts ?? {});

        const total = node.querySelector('.shop-filters__count');

        if (total) {
            /* Already a sentence when it arrives. Polish has three plural forms
             * and the endpoint formatted the number with the ones WordPress
             * knows, so there is nothing to assemble here. */
            total.textContent = payload.total_label ?? String(payload.total);
            total.dataset.total = String(payload.total);
        }

        /* Tell whatever else is listening that this is a new result set. The
         * pagination block resets its page and its totals on this; it shares no
         * file with this one, so an event is the whole interface between them. */
        document.dispatchEvent(
            new CustomEvent('shop:filtered', {
                detail: {
                    total: payload.total,
                    page: payload.page,
                    shown: grid.querySelectorAll('.product-card').length,
                },
            })
        );

        /* pushState, so the filter can be sent as a link and the back button
         * returns to the previous selection rather than leaving the shop. */
        const url = new URL(window.location.href);

        url.search = params.toString();
        window.history.pushState({ filters: params.toString() }, '', url);
    } catch {
        HTMLFormElement.prototype.submit.call(node);
        return;
    }

    node.removeAttribute('aria-busy');
}

/**
 * Write the answer's counts back onto the options.
 *
 * @param {HTMLFormElement} node   The filter form.
 * @param {Object}          answer Taxonomy => slug => count.
 * @returns {void}
 */
function counts(node, answer) {
    /* The endpoint keys counts by taxonomy (`pa_material`); the markup knows
     * facets by their short name (`material`). Match on the tail so neither
     * side has to carry the other's spelling. */
    const byFacet = new Map();

    for (const [taxonomy, slugs] of Object.entries(answer)) {
        byFacet.set(taxonomy.replace(/^pa_/, '').replace(/^product_cat$/, 'category'), slugs);
    }

    for (const field of node.querySelectorAll('.shop-filters__field[data-facet]')) {
        const slugs = byFacet.get(field.dataset.facet);

        if (!slugs) {
            continue;
        }

        for (const option of field.querySelectorAll('.shop-filters__option')) {
            const slug = option.querySelector('input')?.value ?? '';
            const n = slugs[slug] ?? 0;

            option.querySelector('.shop-filters__option-count').textContent = n.toLocaleString();
            option.dataset.empty = n ? 'false' : 'true';
        }
    }
}

if (form) {
    form.dataset.auto = 'true';

    form.addEventListener('change', (event) => {
        if (event.target.matches('.shop-filters__option input, [name="sort"]')) {
            tally(form);
            apply(form);
        }
    });

    /*
     * A dropdown is dismissed by looking away from it. Without this the only way
     * to close one is to hit its own summary again, which nobody tries — they
     * click the page and expect it to go.
     */
    document.addEventListener('click', (event) => {
        for (const facet of form.querySelectorAll('.shop-filters__field[open]')) {
            if (!facet.contains(event.target)) {
                facet.open = false;
            }
        }
    });

    /* Escape closes the open one, which is what a keyboard expects of a menu. */
    form.addEventListener('keydown', (event) => {
        if ('Escape' !== event.key) {
            return;
        }

        for (const facet of form.querySelectorAll('.shop-filters__field[open]')) {
            facet.open = false;
            facet.querySelector('.shop-filters__summary')?.focus();
        }
    });

    /*
     * One at a time, for browsers that do not honour `name` on <details> yet.
     * Harmless where they do.
     */
    if (!('name' in document.createElement('details'))) {
        for (const facet of form.querySelectorAll('.shop-filters__field')) {
            facet.addEventListener('toggle', () => {
                if (!facet.open) {
                    return;
                }

                for (const other of form.querySelectorAll('.shop-filters__field[open]')) {
                    if (other !== facet) {
                        other.open = false;
                    }
                }
            });
        }
    }

    tally(form);

    /* The mobile drawer. Above 768 the CSS shows the row regardless, so this
     * attribute only means anything where the button is visible. */
    const toggle = form.querySelector('.shop-filters__toggle');

    toggle?.addEventListener('click', () => {
        const open = 'true' === toggle.getAttribute('aria-expanded');

        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        form.dataset.open = open ? 'false' : 'true';
    });

    /* Back and forward move between selections rather than out of the shop.
     * Re-reading from the server keeps the ticks, the counts and the grid in
     * agreement — restoring them from history state could not. */
    window.addEventListener('popstate', () => {
        window.location.reload();
    });

    chips(form);
}

/*
 * The bar's entrance. Separate from everything above, which is the filtering
 * itself: this only decides when the motion plays, and the motion is CSS, so
 * the global prefers-reduced-motion guard in main.scss covers it.
 *
 * The bar renders complete and usable; the attribute is what opts it into the
 * entrance. A browser without IntersectionObserver never gets the attribute and
 * shows the finished bar — which matters more here than on a band of text,
 * because these are controls somebody has to be able to reach.
 */
const bars = document.querySelectorAll('.shop-filters');

if (bars.length && 'IntersectionObserver' in window) {
    const revealer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    entry.target.setAttribute('data-reveal', 'in');
                    revealer.unobserve(entry.target);
                }
            }
        },
        { rootMargin: '0px 0px -10% 0px' }
    );

    for (const bar of bars) {
        bar.setAttribute('data-reveal', 'pending');
        revealer.observe(bar);
    }
}
